/**
 * Wire types for the programme dashboard. Mirrored from
 * `packages/api/src/programme/programme.types.ts` (live overview) and
 * `docs/programme-dashboard/programme-state.schema.json` (the state document).
 * The API types are the truth for live responses; the schema is the truth for
 * the document. This file does not import from `packages/api`.
 */

export type LevelId = 'I' | 'II' | 'III';

export type ProgrammeLevelFilter = LevelId | 'all';

export type Stage =
  | 'targeted'
  | 'designed'
  | 'drafted'
  | 'gated'
  | 'reviewed'
  | 'import_ready'
  | 'imported'
  | 'in_review'
  | 'approved'
  | 'executing'
  | 'executed'
  | 'rollback_failed'
  | 'rolled_back'
  | 'cancelled'
  | 'refused'
  | 'discarded'
  | 'live';

export type ContentKind =
  | 'ml'
  | 'elpi'
  | 'cupi'
  | 'nelpi'
  | 'cumulative'
  | 'module'
  | 'mixed'
  | 'unknown'
  | 'none';

export type Operation =
  | 'create'
  | 'repair'
  | 'retire'
  | 'rework'
  | 'pinpoint_fix'
  | 'augment'
  | 'hide'
  | 'move'
  | 'rename'
  | 'reorder'
  | 'archive'
  | 'evaluate'
  | 'qa'
  | 'other';

export type KindSource = 'slug' | 'clusterKey' | 'declared' | 'none';

export type DocumentKindSource = 'slug' | 'clusterKey' | 'document' | 'unknown';

export type WorkTypeId =
  | 'elpi_new'
  | 'cupi_new'
  | 'nelpi_new'
  | 'cumulative_new'
  | 'pi_repair'
  | 'pi_retire'
  | 'pi_augment'
  | 'ml_new'
  | 'ml_rework'
  | 'ml_pinpoint_fix'
  | 'ml_augment_practice'
  | 'ml_retire'
  | 'ml_hide'
  | 'structural_move'
  | 'structural_rename'
  | 'structural_reorder'
  | 'structural_archive'
  | 'diagram_redraw'
  | 'discard_evaluation'
  | 'qa'
  | 'other';

export type ProgrammeCellUnit =
  | 'work_items'
  | 'content_units'
  | 'lessons'
  | 'tasks'
  | 'modules'
  | 'designs';

export interface ProgrammeScanMeta {
  complete: boolean;
  pages: number;
  itemsRead: number;
  consumedCapacityUnits: number;
  elapsedMs: number;
  reason: string | null;
}

export interface ProgrammeCell {
  level: LevelId;
  courseKey: string;
  workType: string;
  contentKind: ContentKind;
  operation: Operation;
  kindSource: KindSource;
  stage: Stage;
  count: number;
  unit: 'work_items';
}

export interface ProgrammeUnmappedTriple {
  taskType: string;
  op: string;
  targetType: string;
  courseKey: string;
  level: LevelId | null;
  slug: string | null;
  reason: string;
  count: number;
}

export interface ProgrammeUnmappedStatus {
  status: string;
  count: number;
}

export interface ProgrammeUnknownCourse {
  courseKey: string;
  count: number;
  reason: string;
}

export interface ProgrammeLevelRow {
  level: LevelId;
  courseKeys: string[];
}

export interface AutonomyInterval {
  lower: number;
  upper: number;
  method: 'clopper-pearson-two-sided-95';
}

export type AutonomyVerdict =
  | 'no data'
  | 'no bar cleared'
  | 'clears 2%'
  | 'clears 5%'
  | 'clears 10%'
  | 'clears 20%';

export type AutonomyEvidence = 'none' | 'no evidence' | 'measured';

export interface AutonomyReviewerCount {
  reviewerId: string;
  n: number;
}

export const AUTONOMY_HEADLINE =
  'measured reviewer outcomes — autonomy is conditional on reviewer detection' as const;

export interface AutonomyRow {
  level: LevelId;
  workType: string;
  family: string;
  n: number;
  refusals: number;
  indeterminate: number;
  passes: number;
  edits: number;
  editRate: number | null;
  refusalRateRange: [number, number] | null;
  interval: AutonomyInterval | null;
  verdict: AutonomyVerdict;
  governingBar: 0.02;
  evidence: AutonomyEvidence;
  controlsExcluded: number;
  machineDecidedExcluded: number;
  otherReviewerExcluded: number;
  /** Items with no decision yet (`pending`) — outside `n`, never counted as unknown outcomes. */
  notYetDecided: number;
  reviewers: AutonomyReviewerCount[];
  controls: null;
  headline: typeof AUTONOMY_HEADLINE;
}

export interface AutonomyRead {
  population: number;
  eventsRead: number;
  capped: boolean;
  cap: number;
}

export type ProgrammeOverviewScope = 'all_courses' | 'granted_courses';

export interface ProgrammeOverview {
  measuredAt: string;
  scope: ProgrammeOverviewScope;
  grantedCourseKeys: string[];
  scan: ProgrammeScanMeta;
  levels: ProgrammeLevelRow[];
  cells: ProgrammeCell[];
  unmapped: ProgrammeUnmappedTriple[];
  unmappedStatuses: ProgrammeUnmappedStatus[];
  unknownCourses: ProgrammeUnknownCourse[];
  autonomy: AutonomyRow[];
  autonomyRead: AutonomyRead;
  stateDocumentDigest: string | null;
}

export interface ProgrammeStateGetResponse {
  state: Record<string, unknown>;
  version: string;
  generatedAt: string;
  publishedAt: string;
  ageSeconds: number;
  oldestSourceMeasuredAt: string | null;
}

export interface ProgrammeStateAbsentResponse {
  state: null;
  reason: 'never_published';
}

export type ProgrammeStateResponse = ProgrammeStateGetResponse | ProgrammeStateAbsentResponse;

export interface ProgrammeStatePublishResponse {
  version: string;
  generatedAt: string;
  publishedAt: string;
  digest: string;
}

export type SourceStatus = 'read' | 'unread';

export interface ProgrammeStateSource {
  name: string;
  path: string;
  status: SourceStatus;
  measuredAt?: string | undefined;
  digest?: string | undefined;
  reason?: string | undefined;
  complete?: boolean | undefined;
}

export interface ProgrammeStateLevel {
  level: LevelId;
  authoringCourseKey: string;
  servingCourseKeys: string[];
  label: string;
}

export type WorkTypeOrigin = 'new' | 'modify' | 'move' | 'none';

export interface ProgrammeStateWorkType {
  id: string;
  label: string;
  description: string;
  origin: WorkTypeOrigin;
  contentKind: ContentKind;
  operation: Operation;
}

export interface ProgrammeStateCell {
  level: LevelId;
  section: string | null;
  workType: string;
  stage: string;
  count: number;
  measuredAt: string;
  source: string;
  unit: ProgrammeCellUnit;
  tentative?: boolean | undefined;
  note?: string | undefined;
  refusals?: Record<string, number> | undefined;
  kindSource?: DocumentKindSource | undefined;
}

export interface ProgrammeStateTarget {
  level: LevelId;
  section?: string | null | undefined;
  workType: string;
  floor: number | null;
  full: number | null;
  basis: string;
  measuredAt: string;
  source: string;
  unmappedRequirements?: number | null | undefined;
  unit?: string | undefined;
}

export interface ProgrammeStateGap {
  level: LevelId;
  section?: string | null | undefined;
  workType: string;
  remaining: number | null;
  tentativeDiscard?: number | null | undefined;
  note?: string | undefined;
  measuredAt: string;
  source: string;
  basis: string;
  inputs?: string[] | undefined;
  priority?: number | null | undefined;
  discardCandidates?: number | null | undefined;
  discarded?: number | null | undefined;
  reason?: string | undefined;
  unit?: string | undefined;
}

export interface ProgrammeStateStructuralRow {
  level: LevelId;
  workType: string;
  proposed: number | null;
  approved: number | null;
  executed: number | null;
  measuredAt: string;
  source: string;
}

export type DefectExposedBy = 'scott' | 'gate' | 'preexisting' | 'mixed' | 'unknown';

export type DefectDimension = 'academic' | 'style' | 'verbosity' | 'structure' | 'process' | 'unknown';

export type DefectObservation = 'exposed' | 'anticipated';

export type DefectDisposition = 'process-fix' | 'redundancy-handled' | 'open';

export type DefectDetectionStatus = 'mechanical' | 'model' | 'none' | 'unknown';

export type DefectVerificationKind = 'rate_remeasured' | 'none' | 'dated_unverified';

export interface ProgrammeStateDefect {
  classId: string;
  name: string;
  firstSeen: string | null;
  lastSeen?: string | null | undefined;
  occurrences: number | null;
  exposedBy: DefectExposedBy;
  dimension: DefectDimension;
  detection: {
    gate: string;
    status: DefectDetectionStatus;
  };
  remediation: string;
  disposition: DefectDisposition;
  description?: string | undefined;
  observation: DefectObservation;
  definitionVersion?: string | null | undefined;
  evidence?: string[] | undefined;
  verification: {
    kind: DefectVerificationKind;
    on: string | null;
    note?: string | undefined;
  };
}

export interface ProgrammeStateControls {
  decided: number;
  caught: number;
  unresolved: number;
  classesCovered?: string[] | undefined;
  note?: string | undefined;
  source: string;
  measuredAt: string;
}

export interface ProgrammeStateDocument {
  schema: 'programme-state/1';
  generatedAt: string;
  generatedBy: string;
  sources: ProgrammeStateSource[];
  levels: ProgrammeStateLevel[];
  workTypes: ProgrammeStateWorkType[];
  stages: string[];
  cells: ProgrammeStateCell[];
  targets: ProgrammeStateTarget[];
  gaps: ProgrammeStateGap[];
  structural: { byLevel: ProgrammeStateStructuralRow[] };
  draftingRuns: unknown[];
  defectRegister: ProgrammeStateDefect[];
  notes: string[];
  controls: ProgrammeStateControls | null;
  registerAdapterWarnings: string[];
  unclassifiedBacklog: number | null;
}
